// Optional KaziPeople Music engagement signal for the employee Home. Renders ONLY when there's a
// live, time-bound event — an open challenge/vote — and then offers the Company Mix as a bonus
// quick-play. Self-hides when no challenge is live or music is off; the employee can dismiss it
// (remembered per browser). No listening data — engagement/culture only, never workforce data.
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Play, Music, Trophy, X, ChevronRight } from 'lucide-react';
import { appleMusicProvider } from '@/features/music/lib/appleMusicProvider';
import { usePlayer } from '@/features/music/player/usePlayer';
import type { MusicItem } from '@/features/music/lib/types';
import * as api from '@/services/performanceService';

const HIDE_KEY = 'kp.music.widget.hidden';
const provider = appleMusicProvider();

function appleSongId(url: string): string | null {
  try {
    const u = new URL(url);
    if (!/(^|\.)music\.apple\.com$/.test(u.hostname)) return null;
    const i = u.searchParams.get('i');
    if (i) return i;
    const parts = u.pathname.split('/').filter(Boolean);
    return parts.includes('song') ? parts[parts.length - 1] || null : null;
  } catch { return null; }
}

export const MusicWidget: React.FC = () => {
  const { setQueue } = usePlayer();
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const openMusic = () => navigate(tenantSlug ? `/t/${tenantSlug}/app/music` : '/app/music');

  const [mix, setMix] = useState<api.CompanyMix | null>(null);
  const [challenge, setChallenge] = useState<api.MusicChallenge | null>(null);
  const [hidden, setHidden] = useState(() => { try { return localStorage.getItem(HIDE_KEY) === '1'; } catch { return false; } });

  useEffect(() => {
    if (hidden) return;
    api.getCompanyMix().then(setMix).catch(() => setMix(null));
    api.listMusicChallenges().then((cs) => setChallenge(cs.find((c) => c.status === 'OPEN') || null)).catch(() => setChallenge(null));
  }, [hidden]);

  const hide = () => { try { localStorage.setItem(HIDE_KEY, '1'); } catch { /* ignore */ } setHidden(true); };

  const playMix = () => {
    const items = (mix?.tracks || [])
      .map((t) => { const id = appleSongId(t.canonical_url); return id ? { kind: 'song' as const, id, provider: 'apple_music' as const, title: t.title } : null; })
      .filter(Boolean) as MusicItem[];
    if (items.length) void setQueue(items);
  };

  if (hidden) return null;
  const hasMix = !!mix && mix.tracks.length > 0;
  // Home shows Music only for a LIVE, time-bound engagement event (an open challenge/vote), per the
  // Home/Today principle that engagement signals surface only when relevant — not a persistent
  // Company-Mix card. When a challenge is live we still offer the mix as a bonus quick-play; with no
  // live challenge the widget stays hidden and Company Mix lives in the Music page.
  if (!challenge) return null;

  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground"><Music className="h-4 w-4 text-primary-500" />Music</div>
        <button type="button" aria-label="Hide music widget" title="Hide" onClick={hide} className="rounded p-1 text-muted-foreground hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"><X className="h-3.5 w-3.5" /></button>
      </div>

      {hasMix && (
        <button type="button" onClick={playMix} className="mb-2 flex w-full items-center gap-3 rounded-xl bg-primary-500/10 p-3 text-left hover:bg-primary-500/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-white"><Play className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-foreground">{mix!.title}</div>
            <div className="truncate text-xs text-muted-foreground">{mix!.track_count} songs · your team’s most-loved</div>
          </div>
        </button>
      )}

      {challenge && (
        <button type="button" onClick={openMusic} className="flex w-full items-center gap-3 rounded-xl border border-border p-3 text-left hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
          <Trophy className="h-4 w-4 shrink-0 text-primary-500" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-foreground">{challenge.title}</div>
            <div className="text-xs text-muted-foreground">Active challenge — listen & vote</div>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </button>
      )}

      <button type="button" onClick={openMusic} className="mt-2 w-full text-center text-xs font-medium text-primary-500 hover:underline">Open KaziPeople Music</button>
    </div>
  );
};
